"use client";

import React, { useMemo } from "react";
import type { IPortfolioNewsProps } from "../../../interfaces/NewsFilter.interface";
import NewsList from "../components/NewsList";
import NewsEmptyState from "../components/NewsEmptyState";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";
import List from "../components/List";
import { usePersonalizedNews } from "../hooks/usePersonalizedNews";
import NoResultsFound from "../../NoResultsFound";
import { buildFilterArray } from "../utils/filterUtils";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import {
  campaignName,
  journeyTag,
  productCategory,
  referenceId,
  sprint,
  subSection,
} from "../../../pages/News/newsTrackingConstants";
import { trackFailureScenarioRetryClick } from "../components/trackFailureScenarioRetry";
import { handleRedirection } from "../../../utils/helper";
import { useRouter } from "next/navigation";

const PortfolioNews: React.FC<IPortfolioNewsProps> = ({ filters, authcode, aemContent }) => {
  const router = useRouter();
  const {
    newsData,
    isLoading,
    page,
    handleShowMore,
    handleShowLess,
    refetch
  } = usePersonalizedNews({
    newsType: "portfolio",
    filters,
    limit: aemContent?.limit ? parseInt(aemContent.limit) : 10,
    authCode: authcode
  });

  const handlePortfolioRedirection = () => {
    handleRedirection(aemContent?.portfolio?.redirectionUrl ?? "", router);
  };
  const filter = useMemo(() => buildFilterArray(filters), [filters]);

  if (isLoading && page === 1) {
    return (
      <div className="px-4 py-4">
        {
          [...Array(5)].map((_, index) =>
            <BFLSkeleton key={index} height={70} classes="mb-2" />)
        }
      </div>
    );
  }

  if (authcode && newsData.statusCode !== 90) {
    return (
      <div className="px-4 py-4">
        <BFLApiFailure
          error={aemContent?.apiFailureTitle ?? "Unable to fetch news"}
          errorDescription={aemContent?.apiFailureDescription ?? "There was an issue fetching news for your portfolio. Please try again later."}
          retryButtonText={aemContent?.apiFailureButtonCta ?? "Retry"}
          imageSrc={aemContent?.images?.errorImage}
          imageAlt={aemContent?.apiFailureImageAlt ?? "Error"}
          onRetry={refetch}
          trackingConfig={{
            sectionTitle: `${aemContent?.apiFailureTitle ?? "Portfolio"} unable to load`,
            componentPosition: "3",
            entryPoint: "no_result_found",
            productCategory,
            subSection,
            journeyTag,
            campaignName,
            sprint,
            referenceId,
          }}
          onRetryClick={(params) =>
            trackFailureScenarioRetryClick(params, getCustomerIdForAnalytics)
          }
        />
      </div>
    );
  }

  if (authcode && filter.length === 0 && (!newsData?.list || newsData.list.length === 0)) {
    return (
      <div className="px-4 py-4">
        <NoResultsFound
          title={aemContent?.portfolio.emptyPortfolioTitle}
          description={aemContent?.portfolio.emptyPortfolioDescription}
          buttonText={aemContent?.portfolio.emptyPortfolioButtonCta}
          buttonIcon={aemContent?.portfolio.emptyPortfolioButtonIcon}
          onButtonClick={handlePortfolioRedirection}
          aemImages={aemContent?.images}
        />
      </div>
    );
  }

  if (authcode && newsData.news.length === 0) {
    return (
      <div className="px-4 py-4">
        <NewsEmptyState
          filters={filters}
          title={aemContent?.portfolio.noResultTitle ?? "No results found"}
          description={aemContent?.portfolio?.noResultFilterDescription ?? "Your current filter selection did not match any results."}
          emptyDescription={aemContent?.portfolio?.noResultDescription ?? "No portfolio news available at the moment."}
          aemImages={aemContent?.images}
        />
      </div>
    );
  }

  return (
    <div className="px-4 py-4">

      <List showMoreText={aemContent?.showMoreText} showLessText={aemContent?.showLessText} showLess={newsData.showLess} showMore={newsData.showMore} onShowLess={handleShowLess} onShowMore={handleShowMore}>
        <NewsList
          newsItems={newsData.news}
          variant="compact"
          showCompanies={true}
          timestampLabels={aemContent?.timestampLabels}
        />
        {isLoading && page > 1 && [...Array(5)].map((_, index) =>
          <BFLSkeleton key={index} height={70} classes="mb-2" />)}
      </List>


    </div>
  );
};

export default PortfolioNews;

